import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import { MarkdownLink, type MarkdownLinkVariant } from './MarkdownLink';
import { MarkdownLinkPreviewProvider } from './MarkdownLinkPreviewProvider';
import type { MarkdownLinkPreviewCache } from './markdownLinkPreviewContext';
import {
  extractMarkdownLinkHrefs,
  resolveLinkPreviews,
} from '../utils/linkThumbnail';

/** Shared with {@link MarkdownLink}; `card` is used for product-update surfaces. */
export type MarkdownContentVariant = MarkdownLinkVariant;

const markdownImageClasses: Record<MarkdownContentVariant, string> = {
  default: 'my-2 max-w-full rounded-md border',
  card: 'my-3 max-w-full rounded-lg border border-border/60 shadow-sm',
};

const markdownCardFrameClassName =
  'overflow-hidden rounded-xl border border-border/60 bg-background shadow-sm ring-1 ring-black/[0.03]';
const markdownCardPaddingClassName = 'p-4 sm:p-5';

const buildMarkdownComponents = (
  imageUrls: Record<string, string> | undefined,
  variant: MarkdownContentVariant
): Components => ({
  h1: ({ children }) => (
    <h1 className='text-2xl font-bold tracking-tight mt-4 mb-2 first:mt-0'>
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className='text-xl font-semibold tracking-tight mt-4 mb-2 first:mt-0'>
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className='text-lg font-semibold mt-3 mb-1.5 first:mt-0'>{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className='text-base font-semibold mt-3 mb-1 first:mt-0'>{children}</h4>
  ),
  h5: ({ children }) => (
    <h5 className='text-sm font-semibold mt-2 mb-1 first:mt-0'>{children}</h5>
  ),
  h6: ({ children }) => (
    <h6 className='text-sm font-medium text-muted-foreground mt-2 mb-1 first:mt-0'>
      {children}
    </h6>
  ),
  p: ({ children }) => (
    <p className='mb-2 last:mb-0 whitespace-pre-wrap'>{children}</p>
  ),
  ul: ({ children }) => (
    <ul className='mb-2 list-disc space-y-1 pl-5 last:mb-0'>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className='mb-2 list-decimal space-y-1 pl-5 last:mb-0'>{children}</ol>
  ),
  li: ({ children }) => <li className='whitespace-pre-wrap'>{children}</li>,
  strong: ({ children }) => (
    <strong className='font-semibold text-foreground'>{children}</strong>
  ),
  em: ({ children }) => <em className='italic'>{children}</em>,
  a: ({ href, children }) => (
    <MarkdownLink href={href} variant={variant}>
      {children}
    </MarkdownLink>
  ),
  blockquote: ({ children }) => (
    <blockquote className='mb-2 border-l-2 border-border pl-3 text-muted-foreground italic last:mb-0'>
      {children}
    </blockquote>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return (
        <code className='block font-mono text-sm whitespace-pre-wrap'>
          {children}
        </code>
      );
    }
    return (
      <code className='rounded bg-muted px-1 py-0.5 font-mono text-sm'>
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className='mb-2 overflow-x-auto rounded-md border bg-muted/50 p-3 font-mono text-sm last:mb-0'>
      {children}
    </pre>
  ),
  hr: () => <hr className='my-4 border-border' />,
  img: ({ src, alt }) => {
    const resolvedSrc =
      src && imageUrls?.[src] ? imageUrls[src] : (src ?? undefined);
    if (!resolvedSrc) {
      return null;
    }
    return (
      <img
        src={resolvedSrc}
        alt={alt ?? ''}
        className={markdownImageClasses[variant]}
      />
    );
  },
});

export type MarkdownContentProps = {
  /** Raw markdown source string. */
  content: string;
  className?: string;
  /** Shown when content is empty or whitespace only. */
  emptyPlaceholder?: string;
  /** Maps markdown image paths (file names) to presigned URLs. */
  imageUrls?: Record<string, string>;
  /**
   * `default` — inline prose (editors, admin tools).
   * `card` — bordered frame with roomier images and link preview cards (e.g. What's New dialog).
   */
  variant?: MarkdownContentVariant;
};

/**
 * Renders markdown as formatted HTML (headings, lists, emphasis, links, etc.)
 * using react-markdown with app-consistent Tailwind styling.
 */
export const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
  className = '',
  emptyPlaceholder = '(empty)',
  imageUrls,
  variant = 'default',
}) => {
  const trimmed = content.trim();
  const [linkPreviews, setLinkPreviews] = useState<MarkdownLinkPreviewCache>(
    new Map()
  );

  const components = useMemo(
    () => buildMarkdownComponents(imageUrls, variant),
    [imageUrls, variant]
  );

  const wrapInCardFrame = (node: React.ReactNode) => {
    if (variant !== 'card') {
      return node;
    }
    return (
      <article className={markdownCardFrameClassName}>
        <div className={markdownCardPaddingClassName}>{node}</div>
      </article>
    );
  };

  useEffect(() => {
    const hrefs = extractMarkdownLinkHrefs(trimmed);
    if (hrefs.length === 0) {
      setLinkPreviews(new Map());
      return;
    }

    const controller = new AbortController();

    void resolveLinkPreviews(hrefs, controller.signal).then(previews => {
      if (!controller.signal.aborted) {
        setLinkPreviews(previews);
      }
    });

    return () => {
      controller.abort();
      setLinkPreviews(new Map());
    };
  }, [trimmed]);

  if (!trimmed) {
    return (
      <p className={`text-sm text-muted-foreground ${className}`.trim()}>
        {emptyPlaceholder}
      </p>
    );
  }

  return wrapInCardFrame(
    <div
      className={`text-sm leading-relaxed text-foreground ${className}`.trim()}
    >
      <MarkdownLinkPreviewProvider previews={linkPreviews}>
        <ReactMarkdown components={components}>{trimmed}</ReactMarkdown>
      </MarkdownLinkPreviewProvider>
    </div>
  );
};
